import { useCallback, useEffect, useState } from 'react';
import type { Map as CarteGL } from 'maplibre-gl';
import { useTrace } from './trace';
import { LegendeCarte } from './legende-carte';
import { ReperesCarte } from './reperes-carte';
import { valeursSelectionBarre, valeursTraceBarre } from './barre-etat-valeurs';
import { expressionLargeur, motifDe } from './signatures-plan';
import { AFFICHAGE_DEFAUT, type Affichage } from './affichages-carte';
import { paletteCarte } from './palette-carte.js';
import type { CadreFeuilles } from './panneau-cadastral';
import { GestionnaireCouches } from './gestionnaire-couches';
import { TableAttributaire } from './table-attributaire';
import type { StylePlan } from './styles-plan';
import { Carte, type ContexteCarte, type SelectionCarte } from './carte';
import { PanneauCadastral, type CibleCarte } from './panneau-cadastral';
import { api, appeler, ErreurApi } from './api';

/**
 * LA CARTE CADASTRALE — UN SEUL ÉCRAN, UN SEUL MOTEUR SPATIAL.
 *
 * ⚠️⚠️ CE FICHIER EXISTE PARCE QUE DEUX ÉCRANS SERVAIENT LE MÊME OBJET.
 * Mesuré le 22/09/2026 :
 *
 *   frontend/src/carte.tsx       MapLibre GL + tuiles vectorielles, 21 couches
 *   frontend/src/assemblage.tsx  <canvas> maison, 1 290 lignes, dessin direct
 *
 * Les deux dessinaient les sections. Les deux portaient un texte d'attribution
 * ODbL — l'un CALCULÉ depuis les couches réellement servies, l'autre écrit en
 * dur, et déjà différent. Les deux avaient leur propre cadrage, leur propre
 * zoom, leur propre notion de « la commune courante ». Deux représentations du
 * même objet divergent au premier correctif appliqué à une seule : c'est le
 * défaut du SDK de Connect, forké en deux exemplaires qui ont dérivé.
 *
 * ⚠️ ET LE CANVAS NE POUVAIT PAS TENIR LE VOLUME. `/sections/contours` sert
 * une collectivité entière en UN envoi (267 ko pour Koulé) : il n'y a aucun
 * chemin de là vers les 2 764 453 parcelles et 1 744 535 bâtiments du pays.
 * Les tuiles vectorielles, elles, sont indexées — onze index GIST.
 *
 * ⚠️ CE QUI N'EST PAS FUSIONNÉ, ET POURQUOI. La PLANCHE imprimée
 * (`/sections/:code/planche`) reste un document rendu par le SERVICE, à
 * l'échelle, avec cartouche. Ce n'est pas une seconde carte : c'est la SORTIE
 * de celle-ci. Les confondre produirait un plan « à peu près à l'échelle » —
 * juridiquement nul pour un cadastre.
 */
export function CarteCadastrale(
  { onSelection, parcelleActive, sectionDuTiroir = null, onOuvrirParcelle, onOuvrirAtelier, reglages, peutTracer = false }:
  {
    onSelection: (s: SelectionCarte | null) => void;
    parcelleActive?: string | null;
    /**
     * La section de la parcelle OUVERTE dans le tiroir — pour que le bloc
     * d'impression de l'inspecteur se taise quand le tiroir montre déjà le
     * même choix (item D.995 : deux montages co-visibles de la même planche).
     */
    sectionDuTiroir?: { code: string; circ: number } | null;
    /** Ouvrir l'ATELIER sur une parcelle — la page du métier (D.968). */
    onOuvrirAtelier?: (parcelle: string) => void;
    onOuvrirParcelle?: (id: string) => void;
    /**
     * L'ATELIER EST UN MÉTIER À PART — ET DEPUIS D.968, UNE PAGE À PART.
     *
     * ⚠️ TRANCHÉ PAR LE FONDATEUR LE 24/09/2026, réaffirmé le 11/10 : « ceux
     * qui font de l'édition ont leur propre page ». Cet écran-ci est la
     * CONSULTATION : il ne monte plus aucun outil d'édition — seule la MESURE
     * reste (contrat §4, pour tous), par la barre d'état. `peutTracer` ne
     * garde plus un montage : il ouvre l'AFFORDANCE « ouvrir dans l'atelier »
     * sur la parcelle désignée.
     *
     * ⚠️ ON CONDITIONNE AUX PERMISSIONS QUE LE SERVICE EXIGE (`cad.parcelle ·
     * creer | modifier`), jamais à une étiquette de rôle ; et le défaut est
     * `false` : une permission qu'on n'a pas su lire n'offre rien.
     */
    peutTracer?: boolean;
    reglages?: {
      zoomMinimal?: { divisions: number; sections?: number; parcelles: number; batiments: number };
      zoomNomsDeRue?: number;
      zoomBatiComplet?: number;
      vueConseillee?: {
        centre: [number, number]; zoom: number;
        division: string | null; parcelles: number; motif: string;
      } | null;
      couverture?: {
        parcellesAuPlan: number; parcellesOpposables: number;
        parcellesActives: number; divisionsPeuplees: number;
        /* Servis par /tuiles/reglages ; la légende et son basculeur en vivent. */
        origines?: Record<string, number>;
        batiments?: number; batimentsNonRattaches?: number;
      };
    } | null;
  },
) {
  const [contexte, setContexte] = useState<ContexteCarte | null>(null);
  const [cible, setCible] = useState<CibleCarte | null>(null);
  const [sectionImposee, setSectionImposee] =
    useState<{ cle: string; section: ContexteCarte['section'] } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /**
   * L'INSTANCE MAPLIBRE, REÇUE DE `Carte` — et jamais une seconde carte.
   *
   * ⚠️ UN ÉTAT, PAS UN `ref` : l'outil de tracé doit se monter quand elle
   * arrive. Un `ref` ne redéclenche aucun rendu, et l'outil resterait alors
   * attaché à `null` pour toute la vie de l'écran, sans un message.
   */
  const [carteGL, setCarteGL] = useState<CarteGL | null>(null);
  /**
   * LE GABARIT DES TUILES, SERVI PAR `GET /tuiles/version`.
   *
   * ⚠️⚠️ C'EST LE DERNIER MAILLON DE « la carte se met à jour après une
   * édition ». Les trois autres sont posés : la migration 231 fait monter
   * `ter.version_du_plan` à chaque écriture, la route `/tuiles/v<n>/…` sert la
   * tuile en `immutable`, et `Carte` remplace ses URL quand le gabarit change.
   * Sans CET état, rien ne redemandait jamais la version — et la chaîne
   * entière restait inerte, sans qu'aucune de ses pièces soit fautive.
   */
  const [gabaritTuiles, setGabaritTuiles] = useState<string | undefined>(undefined);
  /**
   * Le zoom courant — l'arbre des couches s'en sert pour dire qu'une couche
   * cochée n'est PAS servie à ce niveau. Sans ce repère, cocher une case et ne
   * rien voir apparaître se lit « la case ne marche pas ».
   */
  const [zoomCourant, setZoomCourant] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!carteGL) return;
    const suivre = () => setZoomCourant(carteGL.getZoom());
    suivre();
    carteGL.on('zoomend', suivre);
    return () => { carteGL.off('zoomend', suivre); };
  }, [carteGL]);

  const relireVersionDuPlan = useCallback(async () => {
    try {
      const v = await api.versionDuPlan();
      setGabaritTuiles(v.gabarit);
    } catch {
      /* ⚠️ ON NE CASSE PAS LA CARTE POUR ÇA. Sans gabarit, `Carte` retombe sur
         le chemin non versionné : une carte à cinq minutes de retard, ce que
         l'écran faisait déjà hier. Un écran noir serait infiniment pire. */
    }
  }, []);

  useEffect(() => { void relireVersionDuPlan(); }, [relireVersionDuPlan]);

  // Le dépôt abouti redemande la version : l'URL change, la tuile est
  // redemandée, la parcelle neuve apparaît. C'est la boucle complète.
  const [etatTrace, gestesTrace] = useTrace(carteGL, relireVersionDuPlan);

  /**
   * LA PARCELLE DÉSIGNÉE SUR LA CARTE — ce qui rend l'atelier atteignable.
   *
   * ⚠️ LE MOTIF « PRÉSENT ET INATTEIGNABLE » SE SURVEILLE ICI. La reprise
   * d'emprise a compté zéro appelant (mesuré le 23/09/2026) avant d'être
   * branchée sur la sélection ; depuis D.968 elle vit dans l'ATELIER, et la
   * sélection d'une parcelle ouvre ici l'affordance « ouvrir dans l'atelier »
   * (barre d'état) — le chemin de l'interface vers le geste ne doit jamais
   * redevenir vide.
   */
  const [parcelleDesignee, setParcelleDesignee] = useState<string | null>(null);
  /** La sélection entière, pour la barre d'état — pas seulement la parcelle. */
  const [selectionCourante, setSelectionCourante] = useState<SelectionCarte | null>(null);
  const recevoirSelection = useCallback((sel: SelectionCarte | null) => {
    setParcelleDesignee(sel?.couche === 'parcelles' ? sel.identifiant : null);
    setSelectionCourante(sel);
    onSelection(sel);
  }, [onSelection]);

  /**
   * CHOISIR UNE COLLECTIVITÉ — depuis la liste du pays, le fil d'Ariane, ou
   * la barre d'état (adopter la position). Imposée sur sa clé, comme la
   * section : une valeur identique re-choisie doit quand même s'appliquer.
   */
  const [collectiviteImposee, setCollectiviteImposee] =
    useState<{ cle: string; collectivite: ContexteCarte['collectivite'] } | null>(null);
  const choisirCollectivite = useCallback((c: ContexteCarte['collectivite']) => {
    setCollectiviteImposee({ cle: `coll-${c?.sourceId ?? 'aucune'}-${Date.now()}`, collectivite: c });
  }, []);
  /** Le GESTE qui remplace l'ancien suivi du centre (D.997) : un clic. */
  const adopterPosition = useCallback(() => {
    const p = contexte?.position;
    if (p) setCollectiviteImposee({ cle: `pos-${p.sourceId}-${Date.now()}`, collectivite: p });
  }, [contexte?.position]);

  /** La voie sous le curseur — la bulle n'est plus la seule à la dire (D.951). */
  const [voieSurvolee, setVoieSurvolee] = useState<string | null>(null);

  /** Les parcelles retenues dans la table, reflétées sur la carte (D.948). */
  const [selectionTable, setSelectionTable] = useState<string[]>([]);
  const recevoirSelectionTable = useCallback((ids: string[]) => setSelectionTable(ids), []);

  /**
   * LE CHEMIN CLAVIER DE LA SÉLECTION — D.948 : « retenir les parcelles de la
   * section » depuis l'inspecteur, sans un seul geste de souris. Les lignes
   * viennent de la BASE (`dansEnveloppe`, jamais les tuiles — coutures), et
   * seules celles de LA section sont retenues : l'enveloppe mord les voisines.
   */
  const [lignesImposees, setLignesImposees] =
    useState<{ cle: string; lignes: import('./table-attributaire').LigneSelection[] } | null>(null);
  const retenirSection = useCallback(async (code: string, circ: number) => {
    setErreur(null);
    try {
      const sec = await appeler<{ contour: { type: string; coordinates: unknown } }>(
        `/sections/${code}?collectivite=${circ}`);
      let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
      const parcourir = (n: unknown): void => {
        if (!Array.isArray(n)) return;
        if (typeof n[0] === 'number' && typeof n[1] === 'number') {
          const [x, y] = n as [number, number];
          if (x < minLon) minLon = x; if (x > maxLon) maxLon = x;
          if (y < minLat) minLat = y; if (y > maxLat) maxLat = y;
          return;
        }
        for (const e of n) parcourir(e);
      };
      parcourir(sec.contour.coordinates);
      const centre: [number, number] = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
      const col = await api.parcelles.dansEnveloppe(
        { ouest: minLon, sud: minLat, est: maxLon, nord: maxLat }, 500, true);
      const lignes = ((col as any)?.features ?? [])
        .filter((f: any) => String(f?.properties?.section ?? '') === code)
        .map((f: any) => ({
          id: String(f?.properties?.identifiant ?? f?.id ?? ''),
          couche: 'parcelles',
          proprietes: (f?.properties ?? {}) as Record<string, unknown>,
          lon: centre[0], lat: centre[1],
        }))
        .filter((l: { id: string }) => l.id !== '');
      setLignesImposees({ cle: `section-${circ}-${code}-${Date.now()}`, lignes });
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    }
  }, []);

  /**
   * LE CADRE DU TIRAGE — D.980. Les emprises viennent de la RÉPONSE de
   * `/sections/:code/feuilles`, celle-là même que le PDF emploiera : l'écran
   * ne recalcule RIEN — un découpage recalculé ici divergerait du moteur de
   * planche au premier réglage, et le cadre mentirait sur ce qui sort.
   */
  const [cadreFeuilles, setCadreFeuilles] = useState<CadreFeuilles | null>(null);
  /** L'échelle tenue à l'écran, dite par la barre d'état. */
  const [echelleVerrouillee, setEchelleVerrouillee] = useState<number | null>(null);
  /** Tenir l'écran à l'échelle de la planche — adopté sur sa clé (D.945). */
  const [verrouImpose, setVerrouImpose] =
    useState<{ cle: string; denominateur: number | null } | null>(null);
  const tenirEchelle = useCallback((d: number) => {
    setVerrouImpose({ cle: `planche-${d}-${Date.now()}`, denominateur: d });
  }, []);

  useEffect(() => {
    const c = carteGL;
    if (!c) return;
    let vivant = true;
    const SOURCE = 'cadre-feuille';
    const monter = () => {
      if (!vivant || c.getSource(SOURCE)) return;
      const P = paletteCarte();
      c.addSource(SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      c.addLayer({
        id: 'cadre-feuille-trait', type: 'line', source: SOURCE,
        paint: {
          'line-color': P.choix,
          'line-width': expressionLargeur('cadre_feuille') as any,
          'line-dasharray': motifDe('cadre_feuille') as any,
        },
      });
      c.addLayer({
        id: 'cadre-feuille-nums', type: 'symbol', source: SOURCE,
        layout: {
          'text-field': ['get', 'n'], 'text-font': ['Noto Sans Regular'],
          'text-size': 12, 'symbol-placement': 'point',
        },
        paint: { 'text-color': P.choix, 'text-halo-color': P.fond, 'text-halo-width': 1.2 },
      });
    };
    if (c.isStyleLoaded()) monter(); else c.once('load', monter);
    return () => {
      vivant = false;
      for (const id of ['cadre-feuille-trait', 'cadre-feuille-nums']) {
        if (c.getLayer(id)) c.removeLayer(id);
      }
      if (c.getSource(SOURCE)) c.removeSource(SOURCE);
    };
  }, [carteGL]);

  useEffect(() => {
    const c = carteGL;
    const src = c?.getSource('cadre-feuille') as { setData?: (d: unknown) => void } | undefined;
    if (!src?.setData) return;
    src.setData({
      type: 'FeatureCollection',
      features: (cadreFeuilles?.emprises ?? []).flatMap((e) => {
        const centre: [number, number] = [
          e.anneau.reduce((t, p) => t + p[0], 0) / Math.max(1, e.anneau.length),
          e.anneau.reduce((t, p) => t + p[1], 0) / Math.max(1, e.anneau.length),
        ];
        return [
          { type: 'Feature', properties: { n: '' },
            geometry: { type: 'LineString', coordinates: e.anneau } },
          { type: 'Feature', properties: { n: `feuille ${e.feuille}` },
            geometry: { type: 'Point', coordinates: centre } },
        ];
      }),
    });
  }, [carteGL, cadreFeuilles]);

  /* ⚠️ `modifierEmprise` a quitté cet écran avec l'édition (D.968) : la
     reprise d'une emprise vit dans l'atelier. Ici, la parcelle désignée ouvre
     l'affordance « ouvrir dans l'atelier » de la barre d'état. */
  /**
   * LEQUEL DES DEUX PLANS EST AFFICHÉ.
   *
   * ⚠️ L'ÉTAT VIT ICI, PAS DANS LE PANNEAU. Le panneau cadastral est un
   * REGISTRE — tableau d'assemblage, statistiques, fiche de section, tirages ;
   * un réglage de rendu n'y a pas sa place, et l'y mettre lierait le plan
   * affiché à l'onglet ouvert. Ici il est au même niveau que `cible` et
   * `sectionImposee` : ce que la carte regarde, et comment elle le dit.
   */
  const [stylePlan, setStylePlan] = useState<StylePlan>('etat');
  /** PC§27 — l'AFFICHAGE (cadastral/3D/satellite/orthophoto), même doctrine :
      l'état vit dans le composeur, la commande dans la légende dockée. */
  const [affichage, setAffichage] = useState<Affichage>(AFFICHAGE_DEFAUT);

  /**
   * ⚠️ MÉMOÏSÉ, ET CE N'EST PAS DU ZÈLE. `carte.tsx` fait dépendre l'effet qui
   * BÂTIT la carte de `onSelection` : un rappel recréé à chaque rendu
   * démonterait et rebâtirait MapLibre en boucle. Le défaut ne se voit pas au
   * typage — il se voit à une carte qui clignote et perd son cadrage.
   */
  const recevoirContexte = useCallback((c: ContexteCarte) => setContexte(c), []);

  /**
   * OUVRIR UNE SECTION DEPUIS LE TABLEAU D'ASSEMBLAGE.
   *
   * ⚠️ LE COUPLE (code, collectivité) EST OBLIGATOIRE, ET C'EST MESURÉ :
   * 1 515 codes de section sur 1 552 sont portés par PLUSIEURS collectivités,
   * et `AAA` existe dans les 331. Demander `/sections/AAA` sans collectivité
   * rend 400 — la route le dit — mais la demander avec la MAUVAISE rendrait
   * une section parfaitement valide, ailleurs dans le pays, sans que rien ne
   * le signale.
   */
  const choisirSection = useCallback(async (code: string) => {
    setErreur(null);
    const circ = contexte?.collectivite?.sourceId;
    if (!code) { setSectionImposee({ cle: `fermer-${Date.now()}`, section: null }); return; }
    if (!circ) { setErreur("Aucune collectivité sous la carte : impossible de désigner une section."); return; }
    try {
      const s = await appeler<{
        id: string; code: string; origine: 'voie' | 'grille'; hectares: string;
        contour: { type: string; coordinates: unknown };
      }>(`/sections/${code}?collectivite=${circ}`);
      setSectionImposee({
        cle: `${circ}-${code}-${Date.now()}`,
        section: {
          id: String(s.id), code: s.code, collectivite: circ,
          origine: s.origine === 'voie' ? 'voie' : 'grille',
          /* ⚠️ La route sert des HECTARES, la carte des mètres carrés : la
             conversion se fait ici, une fois. Deux unités qui se croisent sans
             se nommer produisent un facteur 10 000 qu'aucun type ne voit. */
          surfaceM2: Number(s.hectares) * 10000,
        },
      });
      const centre = centreDe(s.contour);
      if (centre) setCible({ centre, zoom: 14, cle: `sect-${circ}-${code}-${Date.now()}` });
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    }
  }, [contexte?.collectivite?.sourceId]);

  /**
   * LES VALEURS DE LA BARRE D'ÉTAT (D.944) — par le helper PARTAGÉ avec
   * l'atelier (`barre-etat-valeurs.ts`) : deux écrans, un seul calcul.
   * Ici le tracé ne sert que la MESURE : l'outil s'affiche comme tel.
   */
  const trace = valeursTraceBarre(etatTrace, true);
  const barreSelection = valeursSelectionBarre(selectionCourante, contexte?.collectivite ?? null);

  return (
    <div className="carte-cadastrale">
      <Carte
        onSelection={recevoirSelection}
        onContexte={recevoirContexte}
        cible={cible}
        sectionImposee={sectionImposee}
        stylePlan={stylePlan}
        affichage={affichage}
        collectiviteImposee={collectiviteImposee}
        onVoieSurvolee={setVoieSurvolee}
        selectionTable={selectionTable}
        parcelleActive={parcelleActive}
        seuils={reglages?.zoomMinimal}
        zoomNomsDeRue={reglages?.zoomNomsDeRue}
        vueConseillee={reglages?.vueConseillee}
        couverture={reglages?.couverture}
        onCarte={setCarteGL}
        gabaritTuiles={gabaritTuiles}
        /* Pendant le tracé, un clic pose un sommet — il n'ouvre pas la fiche
           de la parcelle survolée. Voir le garde dans `carte.tsx`. */
        suspendu={etatTrace.mode !== 'inactif'}
      />
      {/**
        * L'IMPRESSION N'EST PLUS MONTÉE ICI — item D.995, retiré le 24/09/2026.
        *
        * ⚠️⚠️ ON NE RASTERISE PAS LA CARTE, ET C'EST LA RÈGLE LA PLUS DURE DE
        * CE DÉPÔT SUR CE SUJET. Un export d'écran rendrait un plan « à peu
        * près à l'échelle » — juridiquement NUL pour un cadastre. La planche
        * est composée par le SERVICE (`GET /sections/:code/planche`), à
        * l'échelle vraie, avec son cartouche, son découpage en feuilles et son
        * rattachement documentaire. Cette règle ne bouge pas ; c'est le POINT
        * DE MONTAGE qui bouge.
        *
        * ⚠️ CE QUI ÉTAIT FAUX ICI : `ImprimerLaPlanche` est monté à trois
        * endroits pour ne pas recopier sa logique — et c'est juste. Mais rien
        * n'empêchait DEUX de ces montages d'être VISIBLES ensemble : dès qu'une
        * section était en contexte, l'agent voyait le même choix de format et
        * les mêmes liens de feuille à deux endroits de l'écran, ici en flottant
        * et là dans le panneau. Constaté sur capture du fondateur, section AGC.
        * Le commentaire d'origine disait « un seul composant, deux points de
        * montage » : l'intention visait la RÈGLE, elle est tenue — c'est
        * l'affichage simultané qui n'avait pas été prévu.
        *
        * ⚠️ ET LE FLOTTANT DÉBORDAIT DE SA BOÎTE : `.atelier-impression` était
        * posé en absolu, 246 px de large, plafonné à 40 % de hauteur — « Aperçu
        * (SVG, ne vaut pas document) » sortait du cadre. Un panneau tronqué se
        * lit comme une panne de rendu, pas comme un panneau trop petit.
        *
        * Le montage qui reste sur cet écran est celui du panneau cadastral
        * (`panneau-cadastral.tsx`), sous la même condition — une section en
        * contexte. Rien n'est perdu : il est dans l'inspecteur, qui est DANS la
        * grille, donc il ne recouvre plus le plan.
        */}
      <GestionnaireCouches carte={carteGL} zoomCourant={zoomCourant} />
      {/* ⚠️ La légende est un panneau DOCKÉ (zone `legende`), plus une
          surcouche — D.943. Elle porte aussi le basculeur état/qualité : une
          légende et le choix du plan qu'elle décrit vivent ensemble. */}
      <LegendeCarte
        stylePlan={stylePlan}
        onStylePlan={setStylePlan}
        affichage={affichage}
        onAffichage={setAffichage}
        couverture={reglages?.couverture}
        zoom={zoomCourant ?? 12}
      />
      {/* La barre d'état — les sept valeurs, toujours présentes (D.944), et
          la POSITION, qui ne sélectionne rien tant qu'on ne l'adopte pas
          (D.997). */}
      <ReperesCarte
        carte={carteGL}
        onEchelleVerrouillee={setEchelleVerrouillee}
        verrouImpose={verrouImpose}
        position={contexte?.position ?? null}
        onAdopterPosition={adopterPosition}
        outil={trace.outil}
        accroche={trace.accroche}
        mesure={trace.mesure}
        selection={barreSelection.texte}
        opposabilite={barreSelection.opposabilite}
        /* La MESURE est le seul outil de la consultation (contrat §4) : ses
           commandes vivent dans la barre d'état, dockées — D.943/D.968. */
        outilMesure={{
          actif: etatTrace.mode !== 'inactif',
          nature: etatTrace.nature === 'surface' ? 'surface' : 'ligne',
          demarrer: (n) => gestesTrace.demarrer(n),
          arreter: () => gestesTrace.arreter(),
        }}
        actionSelection={peutTracer && parcelleDesignee && onOuvrirAtelier
          ? { libelle: 'Ouvrir dans l’atelier', executer: () => onOuvrirAtelier(parcelleDesignee) }
          : null}
        voieSurvolee={voieSurvolee}
        seuils={reglages?.zoomMinimal}
        zoomNomsDeRue={reglages?.zoomNomsDeRue}
        zoomBatiComplet={reglages?.zoomBatiComplet}
      />
      {/* ⚠️ LA TABLE EST COUPÉE PENDANT LE TRACÉ. Un clic qui pose un sommet
          ne doit pas AUSSI remplir la sélection : l'agent verrait la table
          grandir à chaque point posé, sans comprendre d'où viennent les
          lignes. C'est la même raison que `suspendu` sur la carte. */}
      <TableAttributaire carte={carteGL} actif={etatTrace.mode === 'inactif'}
        onOuvrir={onOuvrirParcelle}
        onSelectionParcelles={recevoirSelectionTable}
        imposees={lignesImposees} />
      {erreur && <p className="carte-alerte" role="alert">{erreur}</p>}
      <PanneauCadastral
        contexte={contexte}
        onAller={setCible}
        onOuvrirParcelle={onOuvrirParcelle}
        onChoisirSection={(code) => { void choisirSection(code); }}
        onChoisirCollectivite={choisirCollectivite}
        sectionDuTiroir={sectionDuTiroir}
        onFeuilles={setCadreFeuilles}
        echelleEcran={echelleVerrouillee}
        onTenirEchelle={tenirEchelle}
        onRetenirSection={(code, circ) => { void retenirSection(code, circ); }}
      />
    </div>
  );
}

/** Voir `centreDuContour` dans le panneau : même règle, même réserve. */
function centreDe(g: { type: string; coordinates: unknown }): [number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const parcourir = (n: unknown): void => {
    if (!Array.isArray(n)) return;
    if (typeof n[0] === 'number' && typeof n[1] === 'number') {
      const [x, y] = n as [number, number];
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      return;
    }
    for (const e of n) parcourir(e);
  };
  parcourir(g.coordinates);
  return Number.isFinite(minX) ? [(minX + maxX) / 2, (minY + maxY) / 2] : null;
}
